/**
 * TicketDetailCustomerPage - the customer's view of a single ticket.
 *
 * Layout (SPEC §8):
 *  - Breadcrumb back to /tickets.
 *  - Header card with ticket number, StatusBadge, PriorityBadge,
 *    submitted date, brand chip + website button.
 *  - Description block (sanitized HTML or plain text) with "Show more".
 *  - Attachments grid (images thumbnail, others a file icon).
 *  - Conversation thread of CommentBubble entries (15s poll).
 *  - Composer: RichTextEditor + AttachmentDropzone + Post / Resolve /
 *    Reopen actions. Resolve and Reopen open confirmation modals.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Alert, Button, Card, Modal, Spinner } from "@/components/ui";
import api from "@/lib/api";
import {
  RecentlyViewedRail,
  recordRecentlyViewed,
} from "@/components/tickets/RecentlyViewedRail";
import { useAuthStore } from "@/stores/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAddComment, useTicketComments } from "@/hooks/useTicketComments";
import { useReopenTicket } from "@/hooks/useReopenTicket";
import { useResolveTicket } from "@/hooks/useResolveTicket";
import {
  useTicketAttachments,
  useTicketDetail,
} from "@/hooks/useTicketDetail";
import { safeHtmlProps } from "@/lib/sanitize";
import type {
  AttachmentResponse,
  TicketStatus,
} from "@/types/api";

import { AttachmentDropzone } from "@/components/tickets/AttachmentDropzone";
import {
  AuditTimeline,
  focusCommentAnchor,
} from "@/components/tickets/AuditTimeline";
import { CommentBubble } from "@/components/tickets/CommentBubble";
import { CustomerAttachmentTile } from "@/components/tickets/CustomerAttachmentTile";
import { PriorityBadge } from "@/components/tickets/PriorityBadge";
import { RichTextEditor } from "@/components/tickets/RichTextEditor";
import { StatusBadge } from "@/components/tickets/StatusBadge";

/**
 * How many of the newest messages arrive with their bodies loaded.
 *
 * Same value and same reasoning as the agent surface: a long email
 * thread quotes itself, so the customer was being sent megabytes of
 * their own quoted history on every load and every 15-second poll
 * (2.36 MB of customer-visible bodies on prod ticket HK4). Everything
 * older is one click away via "Load Remaining History"; until then its
 * content is not downloaded and not in the DOM.
 *
 * See plans/2026-08-25-customer-portal-comment-windowing.md.
 */
const DEFAULT_BODY_WINDOW = 6;

// Status enum -> human label. Mirrors the agent page's STATUS_OPTIONS
// so the rail's "Status" header reads "Waiting on Customer" instead
// of the raw "waiting_customer" enum value.
const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "waiting_customer", label: "Waiting on Customer" },
  { value: "waiting_third_party", label: "Waiting on Third Party" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "reopened", label: "Reopened" },
];

function statusLabel(status: string): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

// Lowercase-snake -> readable label for the Type chip in the customer
// Properties card. We mirror the agent's TYPE_OPTIONS labels but only
// expose the customer-visible subset (Question / Service Request /
// Problem); agents can still set agent-only types (incl. on a
// customer's ticket), so the map also covers those and an explicit
// fallback handles anything unmapped.
const TYPE_LABELS: Record<string, string> = {
  "Question": "Question",
  "Service Request": "Service Request",
  "Problem": "Problem",
  "Vendor Support": "Vendor Support",
  "Internal Task": "Internal Task",
  "Failed Credit Card Charge": "Failed Credit Card Charge",
  "Platform Development": "Platform Development",
  "DevOps Task": "DevOps Task",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}


export function TicketDetailCustomerPage(): JSX.Element {
  // ``code`` is whatever the URL bar carries - "ku1", "f-104944", or
  // a bare ticket_number for back-compat. The detail fetch resolves
  // any of the three forms; subsequent hooks key on ticket_number
  // (which we read off the loaded ticket) so the network panel keeps
  // showing the numeric paths for /comments / /attachments / etc.
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const detail = useTicketDetail(code ?? "", {
    enabled: (code ?? "").length > 0,
  });

  // After the detail loads, canonicalize the URL bar to the
  // ``public_code`` form (lowercased) so /tickets/200000 -> /tickets/ku1
  // and /tickets/F-104944 stays /tickets/f-104944. ``replace`` so the
  // back button doesn't bounce between the two URLs.
  const ticketPublicCode = detail.data?.public_code;
  useEffect(() => {
    if (!ticketPublicCode || !code) return;
    const canonical = ticketPublicCode.toLowerCase();
    if (code.toLowerCase() !== canonical) {
      navigate(`/tickets/${canonical}`, { replace: true });
    }
  }, [ticketPublicCode, code, navigate]);

  // Cache the numeric ticket_number for the OTHER hooks. Until the
  // detail loads we have no number yet, so those hooks stay disabled.
  const ticketNumber = detail.data?.ticket_number ?? -1;
  const ticketLoaded = ticketNumber > 0;

  usePageTitle(
    detail.data
      ? `${detail.data.public_code} - ${detail.data.subject ?? "Ticket"} - Buyist Support`
      : "Ticket - Buyist Support",
  );

  // Conversation window. False on every arrival: only the newest
  // DEFAULT_BODY_WINDOW bodies are fetched. Reset per ticket so opening
  // one long thread's history does not drag that onto the next ticket.
  const [historyExpanded, setHistoryExpanded] = useState(false);
  useEffect(() => {
    setHistoryExpanded(false);
  }, [ticketNumber]);

  const comments = useTicketComments(ticketNumber, {
    enabled: ticketLoaded,
    bodyWindow: historyExpanded ? null : DEFAULT_BODY_WINDOW,
  });
  const attachments = useTicketAttachments(ticketNumber, {
    enabled: ticketLoaded,
  });
  const addComment = useAddComment(ticketNumber);
  const resolveTicket = useResolveTicket(ticketNumber);
  const reopenTicket = useReopenTicket(ticketNumber);

  const [draftHtml, setDraftHtml] = useState("");
  const [draftText, setDraftText] = useState("");
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  // Earlier attachments the customer chose to re-send with this reply
  // via the "Attach to Response" hover button. Mirrors the agent's
  // draftAttachmentRefs (TicketDetailAgentPage line 635).
  const [draftAttachmentRefs, setDraftAttachmentRefs] = useState<
    AttachmentResponse[]
  >([]);
  const attachedToDraftIds = useMemo(
    () => new Set(draftAttachmentRefs.map((a) => a.id)),
    [draftAttachmentRefs],
  );
  // Composer collapses to a single-line "Type your reply..." pill
  // until the customer clicks into it - same affordance the agent
  // composer ships (TicketDetailAgentPage line 776). Re-expands
  // automatically whenever there's an in-progress draft so a stray
  // collapse never hides typed content.
  const [composerExpanded, setComposerExpanded] = useState(false);
  const hasDraft =
    draftText.trim().length > 0 ||
    draftFiles.length > 0 ||
    draftAttachmentRefs.length > 0;
  const isComposerOpen = composerExpanded || hasDraft;
  const [resolveOpen, setResolveOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);

  // Aggregate distinct To / CC emails across visible comments (SPEC §8).
  // To here is the extra recipients on inbound messages (colleagues the
  // customer addressed alongside support@buyist.com), not the requester.
  const toEmails = useMemo(() => {
    const set = new Set<string>();
    for (const c of comments.data ?? []) {
      for (const e of c.to_emails) set.add(e);
    }
    return Array.from(set);
  }, [comments.data]);
  const ccEmails = useMemo(() => {
    const set = new Set<string>();
    for (const c of comments.data ?? []) {
      for (const e of c.cc_emails) set.add(e);
    }
    return Array.from(set);
  }, [comments.data]);

  // Newest-first sort to match the agent's conversation order (most
  // recent reply at the top, original message at the bottom). The
  // server returns ASC by id; we resort client-side for parity with
  // the agent surface.
  //
  // Rows whose body the server deliberately left unloaded
  // (``body_omitted``) are counted but never rendered: there is no
  // content to put in a bubble. They still feed the To / CC
  // aggregation above, which reads metadata, not bodies.
  const { sortedComments, omittedCount } = useMemo(() => {
    const sorted = [...(comments.data ?? [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const rendered = sorted.filter((c) => !c.body_omitted);
    return {
      sortedComments: rendered,
      omittedCount: sorted.length - rendered.length,
    };
  }, [comments.data]);

  // Group attachments by comment_id once per attachments payload rather
  // than on every render (this page re-renders on every composer
  // keystroke). Ticket-level files (comment_id=null) are pulled out
  // separately for the Original ticket entry.
  const { attachmentsByComment, ticketLevelAttachments } = useMemo(() => {
    const byComment = new Map<string, AttachmentResponse[]>();
    const ticketLevel: AttachmentResponse[] = [];
    for (const a of attachments.data ?? []) {
      if (a.comment_id) {
        const list = byComment.get(a.comment_id) ?? [];
        list.push(a);
        byComment.set(a.comment_id, list);
      } else {
        ticketLevel.push(a);
      }
    }
    return {
      attachmentsByComment: byComment,
      ticketLevelAttachments: ticketLevel,
    };
  }, [attachments.data]);

  // Sanitized once per distinct description instead of once per
  // keystroke, matching the per-bubble memo in CommentBubble.
  const descriptionHtmlProps = useMemo(
    () =>
      safeHtmlProps(detail.data?.description_html ?? "", {
        // Original-ticket body IS the first message in the thread -
        // there's no prior conversation to collapse. The body often
        // opens with <blockquote> or "From:" headers that would
        // otherwise trip the collapser and hide the entire ticket
        // content behind a "..." pill.
        collapseQuotes: false,
      }),
    [detail.data?.description_html],
  );

  // Audit-timeline jump into not-yet-loaded history: load the rest of
  // the thread, then re-run the same jump once the bubble exists.
  // Mirror of TicketDetailAgentPage.
  const [pendingRevealCommentId, setPendingRevealCommentId] = useState<
    string | null
  >(null);
  const handleRequestCommentReveal = useCallback((commentId: string) => {
    setHistoryExpanded(true);
    setPendingRevealCommentId(commentId);
  }, []);
  useEffect(() => {
    if (!pendingRevealCommentId) return;
    const rows = comments.data ?? [];
    const target = rows.find((c) => c.id === pendingRevealCommentId);
    // Not in this thread at all (a deleted comment still leaves timeline
    // rows). Nothing to jump to, ever - stop waiting.
    if (rows.length > 0 && !target) {
      setPendingRevealCommentId(null);
      return;
    }
    // Still the windowed payload; the body has not arrived yet.
    if (!target || target.body_omitted) return;
    if (focusCommentAnchor(pendingRevealCommentId)) {
      setPendingRevealCommentId(null);
    }
  }, [pendingRevealCommentId, comments.data]);

  // Paste-into-composer inline-image upload. Mirror of the agent's
  // uploadInlineImage in TicketDetailAgentPage: POST the blob to
  // the customer /inline-images endpoint, return the public
  // /i/{token} URL the RichTextEditor inserts as a ResizableImage.
  const uploadInlineImage = useCallback(
    async (file: File): Promise<string> => {
      const form = new FormData();
      form.append("file", file, file.name || "pasted-image");
      const response = await api.post<{ url: string }>(
        `/tickets/${ticketNumber}/inline-images`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.url;
    },
    [ticketNumber],
  );

  // Record this view in the per-user localStorage rail so the next
  // visit to /tickets surfaces this ticket as recently-opened.
  // localStorage only - no server round-trip, no read-receipt leak.
  const userId = useAuthStore((s) => s.user?.id);
  const detailData = detail.data;
  useEffect(() => {
    if (!detailData || !userId) return;
    recordRecentlyViewed(userId, {
      ticket_number: detailData.ticket_number,
      public_code: detailData.public_code,
      subject: detailData.subject,
      status: detailData.status,
    });
  }, [detailData, userId]);

  if (!code) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <Alert variant="error" title="Invalid ticket URL">
          The URL does not contain a ticket identifier.
        </Alert>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <Alert variant="error" title="Ticket not found">
          We couldn't load this ticket. It may have been deleted or you may
          not have access.
        </Alert>
        <Link
          to="/tickets"
          className="mt-4 inline-block text-sm text-primary-700 hover:text-primary-800"
        >
          Back to My Tickets
        </Link>
      </div>
    );
  }

  const ticket = detail.data;
  const canResolve =
    ticket.status !== "resolved" &&
    ticket.status !== "closed" &&
    ticket.status !== "spam";
  const canReopen =
    ticket.status === "resolved" || ticket.status === "closed";

  async function handlePostReply() {
    setComposeError(null);
    if (!draftText.trim()) {
      setComposeError("Please type a reply before posting.");
      return;
    }
    try {
      // Upload each draft file FIRST (matches the agent flow: files
      // pre-upload as ticket-level rows, then the reply POST links
      // them to the new comment via attach_existing_ids). Per-file
      // POST so a single rejected file doesn't roll back the others.
      const uploadedIds: string[] = [];
      for (const file of draftFiles) {
        const form = new FormData();
        form.append("file", file, file.name);
        const r = await api.post<AttachmentResponse>(
          `/tickets/${ticketNumber}/attachments`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        uploadedIds.push(r.data.id);
      }

      const attachExistingIds = [
        ...uploadedIds,
        ...draftAttachmentRefs.map((a) => a.id),
      ];

      await addComment.mutateAsync({
        body_text: draftText,
        body_html: draftHtml || null,
        attach_existing_ids: attachExistingIds,
      });
      setDraftHtml("");
      setDraftText("");
      setDraftFiles([]);
      setDraftAttachmentRefs([]);
      // Collapse the composer back to the pill so a successful post
      // returns the surface to the same shape it started in.
      setComposerExpanded(false);
    } catch (e: unknown) {
      setComposeError(
        e instanceof Error ? e.message : "Failed to post your reply.",
      );
    }
  }

  // Mirror of the agent's handleAttachToResponse / unstage / remove
  // handlers (TicketDetailAgentPage lines 1176-1190). "Attach to
  // Response" dedupes by id so clicking the same paperclip twice is
  // a no-op rather than a list duplicate.
  function handleAttachToResponse(attachment: AttachmentResponse) {
    setDraftAttachmentRefs((prev) => {
      if (prev.some((a) => a.id === attachment.id)) return prev;
      return [...prev, attachment];
    });
    // Mirror the agent: clicking the paperclip is also the
    // affordance that says "open the composer" - users who staged
    // attachments without expanding the editor would otherwise
    // wonder where the files went (TicketDetailAgentPage line 1181).
    setComposerExpanded(true);
  }

  function handleUnstageDraftAttachment(attachmentId: string) {
    setDraftAttachmentRefs((prev) => prev.filter((a) => a.id !== attachmentId));
  }

  async function handleRemoveAttachment(attachment: AttachmentResponse) {
    // Optimistically drop from the staged-attachments strip so the UI
    // updates immediately. Backend enforces uploaded_by_user_id gate;
    // on 4xx we restore the strip + surface the error.
    setDraftAttachmentRefs((prev) => prev.filter((a) => a.id !== attachment.id));
    try {
      await api.delete(
        `/tickets/${ticketNumber}/attachments/${attachment.id}`,
      );
      // Refetch attachments + comments so the timeline drops the row.
      await Promise.all([attachments.refetch(), comments.refetch()]);
    } catch (e: unknown) {
      setComposeError(
        e instanceof Error ? e.message : "Failed to remove the attachment.",
      );
      // Restore the staged-attachments strip on failure.
      setDraftAttachmentRefs((prev) => [...prev, attachment]);
    }
  }

  async function handleResolve() {
    await resolveTicket.mutateAsync();
    setResolveOpen(false);
  }

  async function handleReopen() {
    await reopenTicket.mutateAsync();
    setReopenOpen(false);
  }

  return (
    <div className="w-full space-y-4">
      <nav
        aria-label="Breadcrumb"
        className="text-xs text-neutral-500"
      >
        <Link to="/tickets" className="hover:text-neutral-700">
          My Tickets
        </Link>
        <span className="mx-2 text-neutral-300">/</span>
        <span className="text-neutral-700">#{ticket.public_code}</span>
      </nav>

      {/* Two-column layout mirroring the agent detail page (commit
          d683108). Left 2/3 is the description + conversation + reply
          composer. Right 1/3 is the sticky rail with Status header,
          read-only Properties, Audit timeline (customer-mode = major
          events only, no admin link), and Recently viewed. Mobile
          collapses to a single stacked column. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-4 lg:col-span-2">
          {/* Header card */}
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-medium text-neutral-500">
                  <span>#{ticket.public_code}</span>
                  <span aria-hidden="true">-</span>
                  <span>Submitted {fmtDate(ticket.created_at)}</span>
                </div>
                <h1 className="mt-1 text-xl font-semibold text-neutral-900">
                  {ticket.subject}
                </h1>
              </div>
            </div>

            {/* To / CC lists (aggregated from comments). */}
            {(toEmails.length > 0 || ccEmails.length > 0) && (
              <div className="mt-4 space-y-1 border-t border-neutral-100 pt-3 text-xs text-neutral-600">
                {toEmails.length > 0 && (
                  <div>
                    <span className="font-medium text-neutral-700">To:</span>{" "}
                    {toEmails.join(", ")}
                  </div>
                )}
                {ccEmails.length > 0 && (
                  <div>
                    <span className="font-medium text-neutral-700">CC:</span>{" "}
                    {ccEmails.join(", ")}
                  </div>
                )}
              </div>
            )}
            {/* Description + ticket-level attachments deliberately
                NOT shown here. They render in the Original-ticket
                entry at the BOTTOM of the conversation card below,
                matching the agent layout (TicketDetailAgentPage
                line 1824). */}
          </Card>

          {/* Composer (top of the fold). Collapsed by default - the
              full editor only mounts after the customer clicks into
              the pill or returns to a ticket with an in-progress
              draft. Mirrors the agent composer collapse pattern
              (TicketDetailAgentPage line 1519). */}
          <Card>
            {!isComposerOpen && (
              <button
                type="button"
                onClick={() => setComposerExpanded(true)}
                data-testid="composer-collapsed"
                className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-left text-sm text-neutral-500 transition-colors hover:border-neutral-300 hover:bg-neutral-50"
                aria-label="Open reply composer"
              >
                <i
                  className="fa-duotone fa-thin fa-reply"
                  aria-hidden="true"
                />
                <span>Type your reply...</span>
              </button>
            )}

            <div
              className={`space-y-3 ${isComposerOpen ? "" : "hidden"}`}
              data-testid="composer-container"
              data-expanded={isComposerOpen ? "true" : "false"}
            >
              <h2 className="text-sm font-semibold text-neutral-700">
                Your reply
              </h2>
              <RichTextEditor
                value={draftHtml}
                onChange={(html, text) => {
                  setDraftHtml(html);
                  setDraftText(text);
                }}
                minHeight="24rem"
                placeholder="Type your reply..."
                ariaLabel="Reply body"
                // Pasted screenshots upload to /inline-images and are
                // inserted as <img src=URL> ResizableImage nodes -
                // same flow the agent's reply composer uses.
                onUploadImage={uploadInlineImage}
              />
              <AttachmentDropzone
                value={draftFiles}
                onChange={setDraftFiles}
                compact
              />
              {/* Staged "Attach to Response" strip - mirrors the agent
                  composer (TicketDetailAgentPage line 1666). Lists
                  attachments the customer picked from earlier in the
                  conversation; they ride along when the reply posts
                  via attach_existing_ids. */}
              {draftAttachmentRefs.length > 0 && (
                <div
                  data-testid="staged-attachments"
                  className="flex flex-wrap items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
                >
                  <span className="font-medium uppercase tracking-wide">
                    Re-attaching:
                  </span>
                  {draftAttachmentRefs.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-neutral-800 shadow-sm"
                    >
                      <i
                        className="fa-duotone fa-thin fa-paperclip text-neutral-500"
                        aria-hidden="true"
                      />
                      <span className="max-w-[14rem] truncate">{a.filename}</span>
                      <button
                        type="button"
                        onClick={() => handleUnstageDraftAttachment(a.id)}
                        title={`Unstage ${a.filename}`}
                        aria-label={`Unstage ${a.filename}`}
                        className="ml-1 rounded p-0.5 text-neutral-400 hover:bg-red-100 hover:text-red-700"
                      >
                        <i
                          className="fa-duotone fa-thin fa-xmark text-xs"
                          aria-hidden="true"
                        />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {composeError && <Alert variant="error">{composeError}</Alert>}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handlePostReply}
                  isLoading={addComment.isPending}
                >
                  Post Reply
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setComposerExpanded(false);
                    setDraftHtml("");
                    setDraftText("");
                    setDraftFiles([]);
                    setDraftAttachmentRefs([]);
                    setComposeError(null);
                  }}
                  disabled={addComment.isPending}
                >
                  Cancel
                </Button>
                <Link
                  to="/tickets"
                  className="ml-auto text-sm text-neutral-600 hover:text-neutral-800"
                  onClick={(e) => {
                    if (addComment.isPending) e.preventDefault();
                  }}
                >
                  Back to My Tickets
                </Link>
              </div>
            </div>
          </Card>

          {/* Conversation - newest reply at the top, the original
              ticket as the bottom-most entry. Mirrors
              TicketDetailAgentPage lines 1756-1879: group attachments
              by comment_id, render each bubble with its own
              attachments inline, then close with an Original-ticket
              entry that carries description + any ticket-level
              attachments. */}
          <Card>
            <h2 className="text-sm font-semibold text-neutral-700">
              Conversation
            </h2>
            <div className="mt-4 space-y-3">
              {comments.isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Spinner />
                </div>
              )}
              {(() => {
                // Memoized at the top of the component so a composer
                // keystroke does not re-group the attachments.
                // Aliased here to keep the JSX below unchanged.
                const byComment = attachmentsByComment;
                const ticketLevel = ticketLevelAttachments;

                return (
                  <>
                    {sortedComments.map((c) => (
                      <CommentBubble
                        key={c.id}
                        comment={c}
                        attachments={byComment.get(c.id) ?? []}
                        ticketNumber={ticket.ticket_number}
                        currentUserId={userId ?? null}
                        onAttachToResponse={handleAttachToResponse}
                        onRemoveAttachment={handleRemoveAttachment}
                        attachedToDraftIds={attachedToDraftIds}
                      />
                    ))}

                    {/* The rest of the thread, in the position it
                        would occupy chronologically (this list is
                        newest first, so older messages belong under it
                        and above the original). Nothing behind this
                        button has been downloaded - clicking it is a
                        real fetch, not a CSS reveal. */}
                    {omittedCount > 0 && (
                      <button
                        type="button"
                        data-testid="load-remaining-history"
                        onClick={() => {
                          // Already expanded but still short: the fetch
                          // failed. Re-running it is the retry, since
                          // flipping an already-true flag does nothing.
                          if (historyExpanded) {
                            void comments.refetch();
                            return;
                          }
                          setHistoryExpanded(true);
                        }}
                        // Only inert while THIS request is in flight.
                        // The thread also polls every 15 s; disabling on
                        // any fetch would reject clicks that land on a
                        // poll tick.
                        disabled={historyExpanded && comments.isFetching}
                        className="w-full rounded-lg border border-dashed border-neutral-300 bg-white px-4 py-3 text-sm font-medium text-primary-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50 disabled:cursor-progress disabled:text-neutral-500"
                      >
                        {historyExpanded && comments.isFetching ? (
                          <>
                            <Spinner />
                            <span className="ml-2">Loading history...</span>
                          </>
                        ) : (
                          <>
                            <i
                              className="fa-duotone fa-thin fa-clock-rotate-left mr-2"
                              aria-hidden="true"
                            />
                            Load Remaining History ({omittedCount} older{" "}
                            {omittedCount === 1 ? "message" : "messages"})
                          </>
                        )}
                      </button>
                    )}

                    {/* Original ticket as the bottom-most entry -
                        same shape the agent surface ships. Carries
                        the description body + any ticket-level
                        attachments (those with comment_id IS NULL). */}
                    <div
                      data-testid="original-ticket-entry"
                      className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4"
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full bg-neutral-200 px-2 py-0.5 font-medium uppercase tracking-wide text-neutral-700">
                          Original ticket
                        </span>
                        <span className="text-neutral-500">
                          from {ticket.requester_name || "You"}
                        </span>
                        <span className="text-neutral-400">·</span>
                        <span className="text-neutral-500">
                          {fmtDate(ticket.created_at)}
                        </span>
                      </div>
                      <div className="prose prose-sm max-w-none text-sm text-neutral-800">
                        {ticket.description_html ? (
                          <div
                            dangerouslySetInnerHTML={descriptionHtmlProps}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap">
                            {ticket.description_text}
                          </div>
                        )}
                      </div>
                      {ticketLevel.length > 0 && (
                        <div
                          data-testid="original-ticket-attachments"
                          className="mt-3 border-t border-neutral-200 pt-3"
                        >
                          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                            Attachments
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            {ticketLevel.map((a) => (
                              <CustomerAttachmentTile
                                key={a.id}
                                attachment={a}
                                ticketNumber={ticket.ticket_number}
                                currentUserId={userId ?? null}
                                onAttachToResponse={handleAttachToResponse}
                                onRemove={handleRemoveAttachment}
                                isAttachedToDraft={attachedToDraftIds.has(a.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
        </div>

        {/* Right rail: status header, properties (read-only chips
            + Resolve/Reopen action buttons), audit timeline
            (customer mode = major events only, no admin link, no
            Show-All toggle), recently viewed. Docks with
            ``lg:sticky`` per the same pattern as the agent detail
            page. */}
        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
          {/* Status at-a-glance. Uses the same enum-to-label lookup
              the agent surface uses (STATUS_OPTIONS) so the header
              reads "Waiting on Customer" instead of the raw
              "waiting_customer" enum value. */}
          <Card padding="sm" data-testid="rail-status-header">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                  Status
                </div>
                <div className="mt-1 text-lg font-semibold leading-tight text-neutral-900">
                  {statusLabel(ticket.status)}
                </div>
              </div>
              <StatusBadge status={ticket.status} viewer="customer" />
            </div>
          </Card>

          {/* Properties - read-only Priority + Type chips. Status is
              mutable but only through the explicit Resolve / Reopen
              buttons (matches the agent's call on 2026-05-17). No
              Owner / Assignee / Brand - those are agent-only. */}
          <Card>
            <h2 className="text-sm font-semibold text-neutral-700">
              Properties
            </h2>
            <dl className="mt-3 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs font-medium text-neutral-500">
                  Priority
                </dt>
                <dd>
                  <PriorityBadge priority={ticket.priority} />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs font-medium text-neutral-500">
                  Type
                </dt>
                <dd className="text-neutral-700">
                  {ticket.type ? TYPE_LABELS[ticket.type] ?? ticket.type : "-"}
                </dd>
              </div>
            </dl>
            {(canResolve || canReopen) && (
              <div className="mt-4 flex flex-col gap-2 border-t border-neutral-100 pt-3">
                {canResolve && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setResolveOpen(true)}
                  >
                    Mark Resolved
                  </Button>
                )}
                {canReopen && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setReopenOpen(true)}
                  >
                    Reopen
                  </Button>
                )}
              </div>
            )}
          </Card>

          {/* Audit timeline - customer mode hits the scrubbed
              /tickets/{n}/events endpoint, hides the Show-All
              toggle, and skips the admin link. */}
          <Card>
            <AuditTimeline
              ticketId={ticket.id}
              ticketNumber={ticket.ticket_number}
              mode="customer"
              onRequestCommentReveal={handleRequestCommentReveal}
            />
          </Card>

          {/* Recently viewed - localStorage only, scoped per user.
              Self-hides when empty. */}
          <RecentlyViewedRail
            excludeTicketNumber={ticket.ticket_number}
          />
        </div>
      </div>

      {/* Confirmation modals */}
      <Modal
        isOpen={resolveOpen}
        onClose={() => setResolveOpen(false)}
        title="Mark ticket resolved?"
      >
        <p className="text-sm text-neutral-700">
          This tells Buyist Support that your issue is resolved. You can
          reopen the ticket anytime if it comes back.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setResolveOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              void handleResolve();
            }}
            isLoading={resolveTicket.isPending}
          >
            Mark Resolved
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title="Reopen this ticket?"
      >
        <p className="text-sm text-neutral-700">
          The ticket will move back to Open and a Buyist Support agent will
          pick it up.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setReopenOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              void handleReopen();
            }}
            isLoading={reopenTicket.isPending}
          >
            Reopen
          </Button>
        </div>
      </Modal>

    </div>
  );
}

export default TicketDetailCustomerPage;
